import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { distinctUntilChanged, race, Subject } from 'rxjs';
import { BreadcrumbService } from '../../../layout/breadcrumb/breadcrumb.service';
import { PageMetaService } from '../../../core/services/page-meta.service';
import { AsyncPipe } from '@angular/common';
import { UiModule } from '../../../shared/ui/ui.module';
import { NgbNav, NgbNavItem, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { selectGetDeal, selectGetDealError, selectGetDealLoading } from '../../store/get-deal/get-deal.selectors';
import { filter, takeUntil } from 'rxjs/operators';
import { inputIsNotNull } from '../../../data-access/input-is-not-null-or-undefined';
import { GetDealModule } from '../../store/get-deal/get-deal.module';
import { getDealAction } from '../../store/get-deal/get-deal.actions';
import { GetDealStageModule } from '../../deal-stages/store/get-deal-stage/get-deal-stage.module';
import { getDealStageAction } from '../../deal-stages/store/get-deal-stage/get-deal-stage.actions';
import { selectUpdateDealData } from '../../store/update-deal/update-deal.selectors';
import { UpdateDealModule } from '../../store/update-deal/update-deal.module';
import { NoResultsComponent } from '../../../shared/no-results/no-results.component';

@Component({
  selector: 'app-deal-details-layout',
  templateUrl: './deal-details-layout.component.html',
  styleUrls: [ './deal-details-layout.component.scss' ],
  imports: [
    AsyncPipe,
    UiModule,
    NgbNav,
    RouterLinkActive,
    NgbNavLinkBase,
    RouterLink,
    RouterOutlet,
    NgbNavItem,
    GetDealModule,
    GetDealStageModule,
    UpdateDealModule,
    NoResultsComponent,
  ]
})
export class DealDetailsLayoutComponent implements OnInit, OnDestroy {
  isLoading = this.store$.selectSignal(selectGetDealLoading);
  error = this.store$.selectSignal(selectGetDealError);
  private readonly unsubscribe$ = new Subject<void>();

  errorEffect = effect(() => {
    if (this.error()) {
      this.pageMetaService.setTitle('Deal not found');
    }
  });

  constructor(
    public route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private pageMetaService: PageMetaService,
    private store$: Store,
  ) {
  }

  ngOnInit(): void {
    this.breadcrumbService.set(
      {
        name: 'Deals',
        list: [
          {
            caption: 'Deals',
            link: [ 'deals' ],
            active: false,
          }
        ]
      }
    );

    const paramMap$ = this.route.paramMap;
    if (!paramMap$) throw new Error('Unexpected: Missed queryParams');

    paramMap$.subscribe(queryParam => {
      const id = queryParam.get('id');
      if (!id) throw new Error('Unexpected: Missed deal id');
      this.store$.dispatch(getDealAction({
        payload: {
          id,
        },
      }));
    });

    race(
      this.store$.pipe(
        select(selectGetDeal),
        filter(inputIsNotNull)
      ),
      this.store$.pipe(
        select(selectUpdateDealData),
        filter(inputIsNotNull),
      ),
    ).pipe(
      distinctUntilChanged((prev, curr) => prev?.dealStageId === curr?.dealStageId),
      takeUntil(this.unsubscribe$),
    ).subscribe(deal => {
      this.pageMetaService.patchMeta({ accountName: deal.title });
      this.breadcrumbService.addToList(
        {
          caption: deal.title,
          link: [ 'deals', deal._id, 'details' ],
          active: true,
        }
      );
      this.store$.dispatch(getDealStageAction({ id: deal.dealStageId }));
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.breadcrumbService.clear();
    this.pageMetaService.clearMeta();
  }
}
